import type { TrackHandlerPayload } from "@openpanel/sdk";
import { getClientBuildInfo } from "./client-build-info";
import { redactStudyGuideTelemetryUrls } from "./study-guide-url-privacy";

const OPENPANEL_INTERNAL_EVENT_PROPERTY_KEYS = new Set([
  "__path",
  "__referrer",
  "__timestamp",
  "__revenue",
]);

const PROFILE_ONLY_EVENT_PROPERTY_KEYS = new Set([
  "profileId",
  "userId",
  "email",
  "user_email",
  "newEmail",
  "email_auth",
  "email_communication",
  "userEmail",
  "userName",
  "userCreatedDate",
  "userType",
  "studentType",
  "userSubscriptionPlan",
  "userSubscriptionStatus",
  "userSubscriptionPeriodType",
  "userNumOfMonthlyGenerationsLeft",
  "numOfSetsOfCardsGenerated",
  "whereDidUserComeFrom",
  "userReferrerId",
  "userReferrerSharerDevice",
  "didUserComeFromMetaAd",
  "googleCampaignId",
  "tiktokCampaignId",
  "influencerCampaignId",
  "redditCampaignId",
  "utm_source",
  "isUserNew-LessThanOrEqualTo3Days",
  "universityId",
  "universityName",
  "totalXpEarned",
  "currentLevel",
  "currentTreeXp",
  "unlockedTrees",
  "numOfBuddiesUnlocked",
  "characterTeamSelected",
  "characterWithAccessorySelected",
]);

const OPENPANEL_DERIVED_EVENT_PROPERTY_KEYS = new Set([
  "dateTracked",
  "deviceId",
  "deviceType",
  "operatingSystem",
]);

const BARE_ATTRIBUTION_EVENT_PROPERTY_KEYS = new Set([
  "fbclid",
  "gbraid",
  "gclid",
  "li_fat_id",
  "msclkid",
  "rdt_cid",
  "rtd_cid",
  "ttclid",
  "twclid",
  "wbraid",
]);

// OpenPanel's own page fields. The SDK sets them from the page URL and document.referrer, and OpenPanel
// parses their query (`__query.*`), so a click id in them would reach OpenPanel although the property
// sanitizer drops it as a property.
const OPENPANEL_URL_EVENT_PROPERTY_KEYS = ["__path", "__referrer"];

/**
 * The URL without ad click ids (fbclid, gclid, ttclid, …). `utm_*` parameters stay: OpenPanel derives the
 * session's campaign from them. Works on an absolute URL and on a relative route (`/signup?fbclid=…`) alike:
 * only the query changes; the rest, fragment included, is kept as it was.
 */
export function removeAdClickIdsFromUrl(url: string): string {
  const fragmentIndex = url.indexOf("#");
  const urlWithoutFragment = fragmentIndex === -1 ? url : url.slice(0, fragmentIndex);
  const queryIndex = urlWithoutFragment.indexOf("?");
  if (queryIndex === -1) return url;
  const searchParams = new URLSearchParams(urlWithoutFragment.slice(queryIndex + 1));
  const clickIdParamNames = [...searchParams.keys()].filter((paramName) =>
    BARE_ATTRIBUTION_EVENT_PROPERTY_KEYS.has(paramName.toLowerCase())
  );
  if (clickIdParamNames.length === 0) return url;
  for (const clickIdParamName of clickIdParamNames) searchParams.delete(clickIdParamName);
  const query = searchParams.toString();
  const fragment = fragmentIndex === -1 ? "" : url.slice(fragmentIndex);
  return `${urlWithoutFragment.slice(0, queryIndex)}${query ? `?${query}` : ""}${fragment}`;
}

function removeAdClickIdsFromOpenPanelUrlProperties(properties: Record<string, unknown>): Record<string, unknown> {
  const cleanedProperties = { ...properties };
  for (const urlPropertyName of OPENPANEL_URL_EVENT_PROPERTY_KEYS) {
    const urlPropertyValue = cleanedProperties[urlPropertyName];
    if (typeof urlPropertyValue === "string") {
      cleanedProperties[urlPropertyName] = removeAdClickIdsFromUrl(urlPropertyValue);
    }
  }
  return cleanedProperties;
}

function rootPropertyName(propertyName: string): string {
  // Extract the root segment of nested property paths, for example
  // "__query.utm_source" -> "__query" and "unlockedTrees[*]" -> "unlockedTrees".
  return propertyName.split(/[.[\]]/, 1)[0] ?? propertyName;
}

function isLegacyVendorProperty(propertyName: string): boolean {
  if (OPENPANEL_INTERNAL_EVENT_PROPERTY_KEYS.has(propertyName)) {
    return false;
  }
  return (
    propertyName.startsWith("__amplitude") ||
    propertyName.startsWith("amplitude_") ||
    propertyName.startsWith("[Amplitude]") ||
    propertyName.startsWith("initial_") ||
    propertyName.startsWith("mp_") ||
    propertyName.startsWith("$")
  );
}

function isBareAttributionEventProperty(propertyName: string): boolean {
  const rootName = rootPropertyName(propertyName);
  return (
    rootName.startsWith("utm_") ||
    BARE_ATTRIBUTION_EVENT_PROPERTY_KEYS.has(rootName)
  );
}

function shouldDropEventProperty(propertyName: string): boolean {
  return (
    isLegacyVendorProperty(propertyName) ||
    isBareAttributionEventProperty(propertyName) ||
    OPENPANEL_DERIVED_EVENT_PROPERTY_KEYS.has(rootPropertyName(propertyName)) ||
    PROFILE_ONLY_EVENT_PROPERTY_KEYS.has(rootPropertyName(propertyName))
  );
}

function shouldDropProfileProperty(propertyName: string): boolean {
  return isLegacyVendorProperty(propertyName);
}

function sanitizeProperties(
  properties: Record<string, unknown> | undefined,
  shouldDropProperty: (propertyName: string) => boolean
): Record<string, unknown> {
  if (!properties) return {};
  const sanitizedProperties: Record<string, unknown> = {};

  for (const [propertyName, propertyValue] of Object.entries(properties)) {
    // OpenPanel's ingestion path strips null/undefined property values too;
    // profile nulls do not clear existing traits in the OpenPanel merge path.
    if (
      propertyValue === undefined ||
      propertyValue === null ||
      shouldDropProperty(propertyName)
    ) {
      continue;
    }
    sanitizedProperties[propertyName] = propertyValue;
  }

  return sanitizedProperties;
}

export function sanitizeOpenPanelEventProperties(
  properties: Record<string, unknown> | undefined
): Record<string, unknown> {
  return sanitizeProperties(properties, shouldDropEventProperty);
}

export function sanitizeOpenPanelProfileProperties(
  properties: Record<string, unknown> | undefined
): Record<string, unknown> {
  return sanitizeProperties(properties, shouldDropProfileProperty);
}

export function sanitizeOpenPanelPayload(payload: TrackHandlerPayload): boolean {
  if (payload.type === "replay") {
    // The installed recorder sends JSON rrweb events. DOM blocking does not
    // mask the Meta event's href, full-snapshot attributes or later mutations.
    // Reject an unknown/opaque format rather than upload unchecked bytes.
    try {
      const events = JSON.parse(payload.payload.payload);
      if (!Array.isArray(events)) return false;
      payload.payload.payload = JSON.stringify(redactStudyGuideTelemetryUrls(events));
    } catch {
      return false;
    }
  }
  Object.assign(payload, redactStudyGuideTelemetryUrls(payload));
  if (payload.type === "track") {
    const payloadProperties = payload.payload.properties;
    const userId = payloadProperties?.userId;
    if (
      payload.payload.profileId == null &&
      (typeof userId === "string" || typeof userId === "number")
    ) {
      payload.payload.profileId = userId;
    }
    // Product engagement/share events never retain the SDK's implicit page URL or title.
    // Route, source, and action are supplied as bounded enums instead.
    if (payloadProperties && (payload.payload.name.startsWith("Study Guide ") || ["App Share Requested", "App Content Shared"].includes(payload.payload.name))) {
      for (const key of ["__path", "__referrer", "__title", "path", "referrer", "pageUrl", "windowLocation", "url", "title"]) delete payloadProperties[key];
    }
    payload.payload.properties = {
      ...removeAdClickIdsFromOpenPanelUrlProperties(sanitizeOpenPanelEventProperties(payloadProperties)),
      // Canonical runtime metadata wins over any caller-provided values. This
      // filter also covers raw third-party `window.openpanel.track(...)` calls.
      ...getClientBuildInfo(),
    };
  } else if (
    (payload.type === "identify" || payload.type === "group") &&
    payload.payload.properties
  ) {
    payload.payload.properties = sanitizeOpenPanelProfileProperties(
      payload.payload.properties
    );
  }

  return true;
}
